/**
 * Where the API lives. Deliberately not hardcoded: the same bundle has to work
 * when served by vineelernote-server itself (same origin) and when run from the
 * Vite dev server against a droplet.
 *
 * Resolution order, first match wins:
 *   1. localStorage override  — set at runtime, survives reloads, no rebuild
 *   2. VITE_API_BASE_URL      — build-time env
 *   3. same origin            — the production case
 */

const API_BASE_STORAGE_KEY = "vineelernote.apiBaseUrl";

function readStoredBase(): string | null {
  try {
    return window.localStorage.getItem(API_BASE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function stripTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

/** Base URL for all `/api/*` calls. Empty string means "same origin". */
export function getApiBaseUrl(): string {
  const stored = readStoredBase();
  if (stored !== null && stored.trim() !== "") {
    return stripTrailingSlash(stored.trim());
  }

  const fromEnv = import.meta.env.VITE_API_BASE_URL;
  if (typeof fromEnv === "string" && fromEnv.trim() !== "") {
    return stripTrailingSlash(fromEnv.trim());
  }

  return "";
}

/** Runtime override, mainly for pointing a deployed bundle at another server. */
export function setApiBaseUrl(baseUrl: string | null): void {
  try {
    if (baseUrl === null || baseUrl.trim() === "") {
      window.localStorage.removeItem(API_BASE_STORAGE_KEY);
    } else {
      window.localStorage.setItem(API_BASE_STORAGE_KEY, stripTrailingSlash(baseUrl.trim()));
    }
  } catch {
    /* storage unavailable (private mode); fall back to env/same-origin */
  }
}

/** Joins the configured base with an API path such as `/api/notes/recent`. */
export function apiUrl(path: string, params?: Readonly<Record<string, string>>): string {
  const normalisedPath = path.startsWith("/") ? path : `/${path}`;
  const search = params === undefined ? "" : new URLSearchParams(params).toString();
  return `${getApiBaseUrl()}${normalisedPath}${search === "" ? "" : `?${search}`}`;
}
